/** Controls in a row that do their own thing; a click on one never follows the row's link. */
const INTERACTIVE = 'a, button, input, select, textarea, label, summary, [role="button"], [contenteditable="true"]'

/** The row's primary link: one marked `data-row-link`, else its first link. */
function primaryLink(row: Element) {
	return row.querySelector<HTMLAnchorElement>('a[data-row-link]') ?? row.querySelector<HTMLAnchorElement>('a[href]')
}

/**
 * Makes every table row with a link act on it: a click anywhere in the row that isn't on another control (a button,
 * a second link, a field) follows the row's primary link, as if it had been clicked, so client-side routes, new-tab
 * links and the like behave the same. Cmd/Ctrl-click and middle-click open it in a new tab; selecting text in a row
 * does nothing. The matching hover highlight is `tbody tr:has(a[href])` in controls.css. Returns an uninstaller.
 */
export function installRowLinks(root: Document = document) {
	const follow = (e: MouseEvent, newTab: boolean) => {
		if (e.defaultPrevented) return
		const target = e.target instanceof Element ? e.target : null
		const row = target?.closest('tbody tr')
		if (!row || target!.closest(INTERACTIVE)) return
		if (root.getSelection()?.toString()) return
		const link = primaryLink(row)
		if (!link) return
		if (newTab) window.open(link.href, '_blank', 'noopener')
		else link.click()
	}
	const onClick = (e: MouseEvent) => {
		if (e.button === 0) follow(e, e.metaKey || e.ctrlKey)
	}
	const onAuxClick = (e: MouseEvent) => {
		if (e.button === 1) follow(e, true)
	}
	root.addEventListener('click', onClick)
	root.addEventListener('auxclick', onAuxClick)
	return () => {
		root.removeEventListener('click', onClick)
		root.removeEventListener('auxclick', onAuxClick)
	}
}
